import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { OptionalFilter, SideFilterType } from './interface';
import './sideFilters.less';
import {
  clearModalSideFilter,
  clearSideFilter,
} from '../../modules/SearchDatePicker/searchDatePickerSlice';
import { FiFilter } from 'react-icons/fi';
import { isEmpty } from 'lodash';
import { Button } from 'antd';
import { useSearchParams } from 'react-router-dom';
import SideFiltersContent from './SideFiltersContent';
import SideFiltersModal from './SideFiltersModal';
import ClearFiltersButton from './components/ClearFiltersButton';
import { isMobileViewportWidth } from '../../utils/breakpoints';

/**
 * Helper function to parse price string and extract numeric value
 * Example: "460.5953498646932 $" -> 460.5953498646932
 */
const parsePriceString = (priceStr: string): number => {
  if (!priceStr) return Infinity;
  const numericPart = priceStr.match(/[\d.]+/);
  return numericPart ? parseFloat(numericPart[0]) : Infinity;
};

/**
 * Type guard to check if filter is a StarsFilter
 */
const isStarsFilter = (
  filter: OptionalFilter
): filter is {
  attr: string;
  type: 'stars';
  options: { id: number; min: string }[];
} => {
  return filter.type === 'stars' && filter.attr === 'hotel_stars';
};

/**
 * Group duplicate hotel_stars options by id and keep minimum price for each group
 */
const deduplicateHotelStars = (filters: OptionalFilter[]): OptionalFilter[] => {
  return filters.map((filter) => {
    if (isStarsFilter(filter)) {
      // Group options by id
      const groupedById = filter.options.reduce(
        (
          acc: Record<number, { id: number; min: string }[]>,
          option: { id: number; min: string }
        ) => {
          if (option.id !== null && option.id !== undefined) {
            if (!acc[option.id]) {
              acc[option.id] = [];
            }
            acc[option.id].push(option);
          }
          return acc;
        },
        {}
      );

      // For each group, find the option with minimum price
      const deduplicatedOptions = Object.keys(groupedById).map((id) => {
        const options = groupedById[Number(id)];
        if (options.length === 1) {
          return options[0];
        }

        // Find option with minimum price
        return options.reduce(
          (
            minOption: { id: number; min: string },
            currentOption: { id: number; min: string }
          ) => {
            const minPrice = parsePriceString(minOption.min);
            const currentPrice = parsePriceString(currentOption.min);
            return currentPrice < minPrice ? currentOption : minOption;
          }
        );
      });

      return {
        ...filter,
        options: deduplicatedOptions.sort((a, b) => a.id - b.id), // Sort by star rating
      };
    }
    return filter;
  });
};

interface DynamicSorting {
  name: string;
  label: string;
}

export enum SortByNames {
  Price = 'price',
  HotelStars = 'hotel_stars',
  DistanceAsc = 'distance_asc',
  DistanceDesc = 'distance_desc',

  PriceAsc = 'price_asc',
  PriceDesc = 'price_desc',
  HotelStarsAsc = 'hotel_stars_asc',
  HotelStarsDesc = 'hotel_stars_desc',

  DateAsc = 'date_asc',
  DateDesc = 'date_desc',
}

interface Props {
  optional_filters?: OptionalFilter[];
  starsGreaterThanRatingFilter?: boolean;
  pricingMaxLimitAndPlusFilter?: number | undefined;
  withModalScopeParams?: boolean;
  handleOnClear?: () => void;
  isCompact?: boolean;
  isLoading?: boolean;
  hasPartiallyLoaded?: boolean;
  additionalPrice?: number;
  freeCancellationTooltipText?: string;
  dynamicSorting?: DynamicSorting[];
  filterEntityType?: 'dynamic_flights' | 'bundle_flights';
  isReturnFlight?: boolean;
  showPricePerPax?: boolean;
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
  isFromPackageListView?: boolean;
}

// Same breakpoint as useScreenWidth (hooks/useScreenSize) and the rest of B2C.

const SideFilters = ({
  optional_filters,
  starsGreaterThanRatingFilter,
  pricingMaxLimitAndPlusFilter,
  handleOnClear,
  withModalScopeParams,
  isCompact,
  isLoading,
  hasPartiallyLoaded,
  additionalPrice = 0,
  freeCancellationTooltipText,
  dynamicSorting,
  filterEntityType,
  isReturnFlight,
  showPricePerPax,
  localSideFilters,
  setLocalSideFilters,
  isFromPackageListView = false,
}: Props) => {
  // Deduplicate hotel_stars options on the client side
  const processedFilters = useMemo(() => {
    if (!optional_filters) return [];
    return deduplicateHotelStars(optional_filters);
  }, [optional_filters]);

  // const [searchParams, setSearchParams] = useSearchParams();
  // const [sortDirection, setSortDirection] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileWidth, setIsMobileWidth] = useState<boolean>(
    isMobileViewportWidth(window?.innerWidth)
  );
  useEffect(() => {
    const updateIsMobileWidth = (e: any) => {
      setIsMobileWidth(isMobileViewportWidth(e?.target?.innerWidth));
    };
    window.addEventListener('resize', updateIsMobileWidth);
    return () => {
      window.removeEventListener('resize', updateIsMobileWidth);
    };
  }, []);

  const { sideFilters: reduxSideFilters, modalSideFilters } = useSelector(
    (state: RootState) => {
      return state.search;
    }
  );
  const sideFilters = useMemo(
    () => (withModalScopeParams ? modalSideFilters : reduxSideFilters),
    [withModalScopeParams, modalSideFilters, reduxSideFilters]
  );

  const { sideFilters: sideFilters_t, general: general_t } = useSelector(
    (state: RootState) => {
      return state.config.translations;
    }
  );

  const dispatch = useDispatch();

  /**
   * Local-filter mode — the self-contained site-engine search modules, which
   * hold their filters in React state instead of redux + the URL.
   */
  const isLocalFilterMode = !!localSideFilters && !!setLocalSideFilters;

  // Mirrors redux `sideFilters` into the page URL. Skipped in local-filter mode:
  // those modules must never mutate the URL, and redux may still hold filters
  // from an earlier page — which this effect would otherwise write onto a module
  // page that has nothing to do with them (a stray `?directFlight=0` reappearing
  // right after the module cleaned its own params, for instance).
  useEffect(() => {
    if (isFromPackageListView || isLocalFilterMode) {
      return;
    }
    const currentSearchParams = new URLSearchParams(window.location.search);
    const updatedSearchParams = new URLSearchParams();
    currentSearchParams.forEach((value, key) => {
      updatedSearchParams.append(key, value);
    });

    for (const key in sideFilters) {
      if (key === 't' || key === 'for') {
        continue;
      }
      if (sideFilters[key]?.length > 0) {
        // Because we attach hotel name to the query params
        // in order to ensure that input is properly encoded to
        // avoid issues with special characters like &, = etc.
        if (key === 'hotel') {
          const encodedArr =
            sideFilters[key] &&
            sideFilters[key].map((hotelName: string) =>
              encodeURIComponent(hotelName)
            );
          updatedSearchParams.set(key, encodedArr.join(','));
          continue;
        }

        updatedSearchParams.set(
          key,
          Array.isArray(sideFilters[key])
            ? sideFilters[key].join(',')
            : sideFilters[key]
        );
      } else if (typeof sideFilters[key] === 'number') {
        updatedSearchParams.set(key, sideFilters[key]?.toString() ?? '');
      } else {
        updatedSearchParams.delete(key);
      }
    }
    const queryString = updatedSearchParams?.toString();
    if (queryString.length > 0) {
      window.history.pushState({}, '', `?${queryString}`);
    }
  }, [sideFilters]);

  useEffect(() => {
    return () => {
      if (!isEmpty(sideFilters)) {
        const fn = withModalScopeParams
          ? clearModalSideFilter
          : clearSideFilter;
        dispatch(fn({ filterEntityType, isReturnFlight }));
      }
    };
  }, [withModalScopeParams]);

  const [filtersShown, setFiltersShown] = useState(true);

  // Mobile trigger bookkeeping. The button is portaled to <body>, so it has to
  // learn from an in-place sentinel whether this instance is actually on
  // screen (round-trip flights mount one SideFilters per leg and hide the
  // inactive one — previously both buttons stacked on the same spot) and
  // whether it lives inside the map modal (which sits above normal content).
  const sentinelRef = useRef<HTMLSpanElement>(null);
  const [isInstanceVisible, setIsInstanceVisible] = useState(true);
  const [isInsideMap, setIsInsideMap] = useState(false);
  useLayoutEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const visible = el.getClientRects().length > 0;
    if (visible !== isInstanceVisible) setIsInstanceVisible(visible);
    const insideMap = !!el.closest('.map-modal');
    if (insideMap !== isInsideMap) setIsInsideMap(insideMap);
  });

  // Number of active filters, for the "Filters (n)" badge.
  const activeFiltersCount = useMemo(() => {
    const source = isLocalFilterMode ? localSideFilters : sideFilters;
    if (!source) return 0;
    return (optional_filters ?? []).reduce((count, filter: any) => {
      const value = source[filter?.attr];
      const isActive = Array.isArray(value)
        ? value.length > 0
        : typeof value === 'number'
        ? true
        : typeof value === 'string'
        ? value.trim().length > 0
        : !!value;
      return count + (isActive ? 1 : 0);
    }, 0);
  }, [isLocalFilterMode, localSideFilters, sideFilters, optional_filters]);

  // Room under the results so the floating button never covers the last card.
  const isFabShown = isMobileWidth && isInstanceVisible && !!optional_filters?.length;
  useEffect(() => {
    if (!isFabShown) return;
    document.body.classList.add('b2c-has-filters-fab');
    return () => document.body.classList.remove('b2c-has-filters-fab');
  }, [isFabShown]);

  const handleModalToggle = () => {
    if (isMobileWidth) {
      setIsModalOpen(!isModalOpen);
    } else {
      setFiltersShown((prev) => !prev);
    }
  };

  if (!optional_filters || isEmpty(optional_filters)) {
    return null;
  }

  return (
    <>
      {/* Mobile only: floating filters trigger + modal */}
      {isMobileWidth && (
        <>
          <span ref={sentinelRef} className="side-filters-mobile-sentinel" />
          {/*
            Render the floating button into document.body via a portal so its
            `position: fixed` always resolves against the viewport. Otherwise a
            transformed/overflow ancestor (e.g. a results list or map wrapper)
            becomes its containing block and breaks the fixed positioning.
          */}
          {/* hidden while its sheet is open */}
          {isInstanceVisible &&
            !isModalOpen &&
            createPortal(
              <Button
                icon={<FiFilter size={18} />}
                onClick={handleModalToggle}
                className={`btn-mobile-show-filters filter-svg ${
                  filtersShown ? 'btn-style-5' : 'btn-style-4'
                } ${isInsideMap ? 'is-inside-map' : ''}`}
              >
                {general_t.filters}
                {activeFiltersCount > 0 && (
                  <span className="btn-mobile-show-filters__count">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>,
              document.body
            )}

          <SideFiltersModal
            optional_filters={processedFilters}
            starsGreaterThanRatingFilter={starsGreaterThanRatingFilter}
            pricingMaxLimitAndPlusFilter={pricingMaxLimitAndPlusFilter}
            handleOnClear={handleOnClear}
            isLoading={isLoading}
            hasPartiallyLoaded={hasPartiallyLoaded}
            additionalPrice={additionalPrice}
            freeCancellationTooltipText={freeCancellationTooltipText}
            dynamicSorting={dynamicSorting}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            activeFiltersCount={activeFiltersCount}
            filterEntityType={filterEntityType}
            isReturnFlight={isReturnFlight}
            showPricePerPax
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
            parentUsesModalScope={!!withModalScopeParams}
          />
        </>
      )}

      {/* Inline filters for non-compact mode */}
      {!isMobileWidth && (
        <div
          className={`side-filters ${
            filtersShown ? 'hide-filter-on-mobile' : ''
          } ${isCompact ? 'compact' : ''}`}
        >
          <ClearFiltersButton
            optional_filters={processedFilters}
            handleOnClear={() => {
              handleOnClear && handleOnClear();
            }}
            withModalScopeParams={withModalScopeParams}
            inactiveMode="hidden"
            filterEntityType={filterEntityType}
            isReturnFlight={isReturnFlight}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />

          <SideFiltersContent
            optional_filters={processedFilters}
            starsGreaterThanRatingFilter={starsGreaterThanRatingFilter}
            pricingMaxLimitAndPlusFilter={pricingMaxLimitAndPlusFilter}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            hasPartiallyLoaded={hasPartiallyLoaded}
            additionalPrice={additionalPrice}
            freeCancellationTooltipText={freeCancellationTooltipText}
            dynamicSorting={dynamicSorting}
            filterEntityType={filterEntityType}
            isReturnFlight={isReturnFlight}
            showPricePerPax
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        </div>
      )}
    </>
  );
};

export default SideFilters;
